import { establishLicenseSession } from '@alchemiesolns/product-registration';
import type { LoginFormData } from '@alchemiesolns/product-registration';
import { login, reloadCurrentUser, UserAccessError } from '@alchemiesolns/user-access';
import { fetchLicenses } from '@alchemiesolns/store-api';

function userAccessErrorToFriendlyMessage(err: UserAccessError): string {
  // user-access gives us package-owned codes with default messages; we adapt
  // a couple to match the app's phrasing (login CTA says "Incorrect email or
  // password" rather than "Incorrect email or password." with a period).
  switch (err.code) {
    case 'user-not-found':
    case 'wrong-password':
      return 'Incorrect email or password.';
    case 'invalid-email':
      return 'Invalid email address.';
    case 'user-disabled':
      return 'This account has been disabled.';
    case 'too-many-requests':
      return 'Too many attempts. Try again later.';
    default:
      return err.message || 'Sign-in failed.';
  }
}

export type FirebaseLoginResult = { kind: 'licensed' } | { kind: 'needs_upgrade' };

/**
 * Signs in with Firebase Auth (via @alchemiesolns/user-access), then reads
 * the caller's license map from alchemie-commerce (via @alchemiesolns/store-api).
 * If a usable license exists for `productId`, persists the JWT via
 * `establishLicenseSession`. Otherwise returns `needs_upgrade`.
 */
export async function loginWithFirebaseForProduct(
  productId: string,
  formData: LoginFormData
): Promise<FirebaseLoginResult> {
  const bypassBilling = import.meta.env.VITE_BYPASS_BILLING === 'true';
  const email = formData.email.trim();
  const password = formData.password;

  try {
    await login(email, password);
  } catch (e) {
    if (e instanceof UserAccessError) {
      throw new Error(userAccessErrorToFriendlyMessage(e));
    }
    throw e;
  }

  const refreshedUser = await reloadCurrentUser();
  if (refreshedUser && !refreshedUser.emailVerified) {
    throw new Error('Please verify your email before signing in. Check your inbox and spam folder.');
  }

  if (bypassBilling) {
    return { kind: 'licensed' };
  }

  return (await fetchAndEstablishLicense(productId)) ? { kind: 'licensed' } : { kind: 'needs_upgrade' };
}

/**
 * After Stripe checkout (or any time a license may have appeared server-side),
 * fetch the caller's license map and establish the license session if usable.
 */
export async function tryEstablishLicense(productId: string): Promise<boolean> {
  return fetchAndEstablishLicense(productId);
}

async function fetchAndEstablishLicense(productId: string): Promise<boolean> {
  let licenses;
  try {
    ({ licenses } = await fetchLicenses());
  } catch {
    return false;
  }

  const summary = licenses[productId];
  if (!summary?.key) return false;
  if (summary.status !== 'active' && summary.status !== 'past_due') return false;

  try {
    await establishLicenseSession(summary.key);
    return true;
  } catch {
    return false;
  }
}
